// Deposits from other chains through Circle CCTP V2: the chains the app offers, Circle's contracts there and the
// burn's parameters. The message layout, Circle's API and the fee math live in offchain/src/cctp.ts. Every
// address here was checked onchain on 2026-10-16: USDC symbol and decimals, TokenMessengerV2 code and its remote
// messenger for HyperEVM (domain 19); Circle deploys the same addresses on every chain.
import { createPublicClient, http, numberToHex, parseAbi, type Address, type Chain, type EIP1193Provider, type PublicClient } from 'viem';
import { arbitrum, arbitrumSepolia, avalanche, base, baseSepolia, mainnet, optimism, polygon, sepolia, unichain } from 'viem/chains';

import { FINALITY, HYPEREVM_DOMAIN, irisFees, irisUrlFor, maxFeeFor } from '../../../offchain/src/cctp.ts';
import { config, deployment } from '../config.ts';

export type Source = {
  domain: number;
  chain: Chain;
  usdc: Address;
  name: string;
  /** When the chain's default public RPC refuses browsers */
  rpc?: string;
};

const TOKEN_MESSENGER_MAINNET: Address = '0x28b5a0e9C621a5BadaA536219b3a228C8168cf5d';
const TOKEN_MESSENGER_TESTNET: Address = '0x8FE6B999Dc680CcFDD5Bf7EB0974218be2542DAA';

const MAINNET: Source[] = [
  { domain: 3, chain: arbitrum, usdc: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831', name: 'Arbitrum' },
  { domain: 6, chain: base, usdc: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', name: 'Base' },
  { domain: 0, chain: mainnet, usdc: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', name: 'Ethereum' },
  { domain: 2, chain: optimism, usdc: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85', name: 'OP Mainnet' },
  { domain: 7, chain: polygon, usdc: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359', name: 'Polygon', rpc: 'https://polygon-bor-rpc.publicnode.com' },
  { domain: 1, chain: avalanche, usdc: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E', name: 'Avalanche' },
  { domain: 10, chain: unichain, usdc: '0x078D782b760474a361dDA0AF3839290b0EF57AD6', name: 'Unichain' },
];
const TESTNET: Source[] = [
  { domain: 3, chain: arbitrumSepolia, usdc: '0x75faf114eafb1BDbe2F0316DF893fd58CE46AA4d', name: 'Arbitrum Sepolia' },
  { domain: 6, chain: baseSepolia, usdc: '0x036CbD53842c5426634e7929541eC2318f3dCF7e', name: 'Base Sepolia' },
  { domain: 0, chain: sepolia, usdc: '0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238', name: 'Ethereum Sepolia' },
];

const testnet = config.chainId === 998;
export const SOURCES: Source[] = testnet ? TESTNET : MAINNET;
/** Circle's TokenMessengerV2 on the source chains */
export const TOKEN_MESSENGER: Address = testnet ? TOKEN_MESSENGER_TESTNET : TOKEN_MESSENGER_MAINNET;
export const IRIS = irisUrlFor(config.chainId);
/** The gateway every burn names as mint recipient and destination caller; none on a chain without one */
export const GATEWAY = deployment.cctpGateway;
/** The route is shown but cannot run: the local chain's gateway talks to a stand-in, a real burn would be lost */
export const BRIDGE_PREVIEW_ONLY = config.isLocal;
/** USDC has 6 decimals on every source chain */
export const USDC_DECIMALS = 6;

export const tokenMessengerAbi = parseAbi([
  'function depositForBurnWithHook(uint256 amount, uint32 destinationDomain, bytes32 mintRecipient, address burnToken, bytes32 destinationCaller, uint256 maxFee, uint32 minFinalityThreshold, bytes hookData)',
]);
export const usdcAbi = parseAbi([
  'function balanceOf(address owner) view returns (uint256)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)',
]);

export function publicClientFor(s: Source): PublicClient {
  return createPublicClient({ chain: s.chain, transport: http(s.rpc) });
}

export type Speed = 'fast' | 'standard';
export type Fees = { fastBps: number; standardBps: number };

export const quoteFees = (s: Source): Promise<Fees> => irisFees(IRIS, s.domain, HYPEREVM_DOMAIN);

/** Circle's fee on `amount` at `bps`, rounded up to a unit (what the credit will be short of) */
export function feeOn(amount: bigint, bps: number): bigint {
  if (bps <= 0) return 0n;
  return (amount * BigInt(Math.round(bps * 100)) + 999_999n) / 1_000_000n;
}

/** The burn's parameters: the cap on Circle's fee and the finality it waits for */
export function burnParams(amount: bigint, speed: Speed, fees: Fees): { maxFee: bigint; minFinalityThreshold: number } {
  return {
    maxFee: maxFeeFor(amount, speed === 'fast' ? fees.fastBps : fees.standardBps),
    minFinalityThreshold: speed === 'fast' ? FINALITY.fast : FINALITY.standard,
  };
}

/** Rough time to the credit: Circle attests a fast transfer within a minute, a standard one at source finality */
export function etaText(s: Source, speed: Speed): string {
  if (speed === 'fast') return 'under a minute';
  // Avalanche finalizes in seconds; Ethereum and the rollups that settle on it wait for L1 finality
  return s.domain === 1 ? 'a minute or two' : 'about 15 to 20 minutes';
}

/** Puts the wallet on `chain`, adding it when the wallet does not know it */
export async function switchWalletChain(provider: EIP1193Provider, chain: Chain): Promise<void> {
  const current = Number(await provider.request({ method: 'eth_chainId' }));
  if (current === chain.id) return;
  const chainId = numberToHex(chain.id);
  try {
    await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId }] });
  } catch (err) {
    if ((err as { code?: number }).code !== 4902) throw err;
    await provider.request({
      method: 'wallet_addEthereumChain',
      params: [
        {
          chainId,
          chainName: chain.name,
          nativeCurrency: chain.nativeCurrency,
          rpcUrls: [...chain.rpcUrls.default.http],
          blockExplorerUrls: chain.blockExplorers ? [chain.blockExplorers.default.url] : undefined,
        },
      ],
    });
  }
}
